// src/cashup/SettingsTab.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  addDoc, collection, doc, setDoc, getDoc,
  orderBy, query, serverTimestamp, updateDoc,
  getDocs, where, limit,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { logAudit } from '../utils/audit';
import useActor from '../utils/useActor';
import { useOrgId } from '../utils/tenant';
import Confirm from '../components/Confirm';
import Modal from '../components/Modal';
import Prompt from '../components/Prompt';
import PhotoAttendanceSettings from './PhotoAttendanceSettings';

// helper: subcollection ref under /orgs/{orgId}/<sub>
const orgCol = (orgId, sub) => collection(db, 'orgs', orgId, sub);

// LR7 — Leave Policy Foundation. Policies live inside the existing compact labor settings
// document, so Settings adds no new collection listener/read path. Existing LR1–6 IDs remain intact.
const isLeaveAttendanceType = (row) => /leave/i.test(String(row?.id || '')) || /leave/i.test(String(row?.label || ''));

const defaultLeavePolicy = (row = {}) => {
  const id = String(row?.id || '').toLowerCase();
  const unpaid = row?.paid === false || id === 'unpaid-leave';
  const annual = id === 'annual-leave';
  const sick = id === 'sick-leave';
  const family = id === 'family-responsibility-leave';
  return {
    enabled: !unpaid,
    accrualAmountDays: annual ? 1.5 : sick ? 1 : family ? 1 : 0,
    accrualFrequency: annual ? 'monthly' : sick ? 'monthly' : family ? 'yearly' : 'none',
    // 0 = inherit the organisation-wide accrual day (default 1st).
    accrualDayOfMonth: 0,
    cycleMonths: family ? 12 : 12,
    maximumBalanceDays: 0,
    carryOver: annual,
    carryOverLimitDays: 0,
    resetAtCycleEnd: !annual && !unpaid,
    evidenceRequired: sick,
    evidenceAfterDays: sick ? 2 : 0,
    blockIfInsufficient: annual,
  };
};

const withLeavePolicyDefaults = (row) => {
  if (!isLeaveAttendanceType(row)) return row;
  return { ...row, leavePolicy: { ...defaultLeavePolicy(row), ...(row?.leavePolicy || {}) } };
};

export default function SettingsTab({ siteScope = { allSites: true, siteIds: [] } }) {
  const orgId = useOrgId();
  const actor = useActor();
  const scopedSiteIds = useMemo(() => Array.from(new Set(siteScope.siteIds || [])).filter(Boolean), [siteScope.siteIds]);
  const isAllSites = siteScope.allSites === true;
  const rowInScope = (row = {}) => {
    if (isAllSites) return true;
    const ids = Array.isArray(row.siteIds) && row.siteIds.length ? row.siteIds : [row.storeId || row.siteId].filter(Boolean);
    return row.allSites !== true && ids.length > 0 && ids.every((id) => scopedSiteIds.includes(id));
  };
  const [activeTab, setActiveTab] = useState('setup');
  const [photoSettingsExpanded, setPhotoSettingsExpanded] = useState(false);

  // — Labor settings (public holidays) —
  const [publicPayEnabled, setPublicPayEnabled] = useState(true);
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [newHoliday, setNewHoliday] = useState('');

  // — Schedule attendance / leave types —
  const DEFAULT_ATTENDANCE_TYPES = [
    { id: 'annual-leave', label: 'Annual Leave', paid: true, allocateHours: true, defaultHours: 8, requiresApproval: true, showOnSchedule: true },
    { id: 'sick-leave', label: 'Sick Leave', paid: true, allocateHours: true, defaultHours: 8, requiresApproval: true, showOnSchedule: true },
    { id: 'family-responsibility-leave', label: 'Family Responsibility Leave', paid: true, allocateHours: true, defaultHours: 8, requiresApproval: true, showOnSchedule: true },
    { id: 'birthday-leave', label: 'Birthday Leave', paid: true, allocateHours: true, defaultHours: 8, requiresApproval: false, showOnSchedule: true },
    { id: 'paid-leave', label: 'Paid Leave', paid: true, allocateHours: true, defaultHours: 8, requiresApproval: true, showOnSchedule: true },
    { id: 'unpaid-leave', label: 'Unpaid Leave', paid: false, allocateHours: false, defaultHours: 0, requiresApproval: true, showOnSchedule: true },
    { id: 'training', label: 'Training', paid: true, allocateHours: true, defaultHours: 8, requiresApproval: false, showOnSchedule: true },
  ];
  const [attendanceTypes, setAttendanceTypes] = useState(() => DEFAULT_ATTENDANCE_TYPES.map(withLeavePolicyDefaults));
  const [newAttendanceLabel, setNewAttendanceLabel] = useState('');
  const [newAttendancePaid, setNewAttendancePaid] = useState(true);
  const [newAttendanceAllocateHours, setNewAttendanceAllocateHours] = useState(true);
  const [newAttendanceHours, setNewAttendanceHours] = useState(8);
  const [newAttendanceApproval, setNewAttendanceApproval] = useState(true);
  const [attendanceSaveState, setAttendanceSaveState] = useState('idle');
  // LR10.1 — organisation-wide leave accrual anchor. 1st is the safe/default payroll-aligned day.
  const [leaveAccrualDayOfMonth, setLeaveAccrualDayOfMonth] = useState(1);
  const [leaveAccrualDaySaveState, setLeaveAccrualDaySaveState] = useState('idle');

  // — Auto clock-out settings —
  const [autoClockOutEnabled, setAutoClockOutEnabled] = useState(true);
  const [autoClockOutExcludedStoreIds, setAutoClockOutExcludedStoreIds] = useState([]);
  const [autoClockOutDisabledWeekdays, setAutoClockOutDisabledWeekdays] = useState([]);
  const [autoClockOutDateRanges, setAutoClockOutDateRanges] = useState([]);
  const [newAutoClockOutFrom, setNewAutoClockOutFrom] = useState('');
  const [newAutoClockOutTo, setNewAutoClockOutTo] = useState('');
  const [abandonedShiftHours, setAbandonedShiftHours] = useState(18);
  const [thresholdSaveState, setThresholdSaveState] = useState('idle');

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;

    // Cost optimisation: labor/leave policy is slow-changing configuration. A permanent
    // realtime listener adds no operational value here, so load the single compact doc once.
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'orgs', orgId, 'settings', 'labor'));
        if (cancelled) return;
        const d = snap.data() || {};
        setPublicPayEnabled(d.publicPayEnabled !== false);
        setPublicHolidays(Array.isArray(d.publicHolidays) ? d.publicHolidays : []);
        setAttendanceTypes(
          Array.isArray(d.attendanceTypes) && d.attendanceTypes.length
            ? d.attendanceTypes.map(withLeavePolicyDefaults)
            : DEFAULT_ATTENDANCE_TYPES.map(withLeavePolicyDefaults)
        );
        setLeaveAccrualDayOfMonth(Math.max(1, Math.min(28, Math.round(Number(d.leaveAccrualDayOfMonth || 1)))));
        setAutoClockOutEnabled(d.autoClockOutEnabled !== false);
        setAutoClockOutExcludedStoreIds(Array.isArray(d.autoClockOutExcludedStoreIds) ? d.autoClockOutExcludedStoreIds : []);
        setAutoClockOutDisabledWeekdays(Array.isArray(d.autoClockOutDisabledWeekdays) ? d.autoClockOutDisabledWeekdays : []);
        setAutoClockOutDateRanges(Array.isArray(d.autoClockOutDateRanges) ? d.autoClockOutDateRanges : []);
        setAbandonedShiftHours(Math.max(18, Math.min(24, Number.isFinite(Number(d.forceAutoClockOutHours ?? d.abandonedShiftHours)) ? Math.round(Number(d.forceAutoClockOutHours ?? d.abandonedShiftHours)) : 18)));
        setThresholdSaveState('idle');
      } catch (e) {}
    })();

    return () => { cancelled = true; };
  }, [orgId]);


  useEffect(() => {
    if (thresholdSaveState !== 'saved') return;
    const t = setTimeout(() => setThresholdSaveState('idle'), 2500);
    return () => clearTimeout(t);
  }, [thresholdSaveState]);

  const saveLaborSettings = async (patch) => {
    if (!orgId) return;
    await setDoc(doc(db, 'orgs', orgId, 'settings', 'labor'), patch, { merge: true });
    try {
      await logAudit(db, actor, {
        action: 'SAVE_LABOR_SETTINGS',
        page: 'Settings',
        entityType: 'labor_settings',
        entityId: 'labor',
      });
    } catch (e) {}
  };


  const persistAttendanceTypes = async (next) => {
    setAttendanceTypes(next);
    setAttendanceSaveState('saving');
    try {
      await saveLaborSettings({ attendanceTypes: next });
      setAttendanceSaveState('saved');
      setTimeout(() => setAttendanceSaveState('idle'), 2200);
    } catch (e) {
      setAttendanceSaveState('error');
    }
  };

  const addAttendanceType = async () => {
    const label = String(newAttendanceLabel || '').trim();
    if (!label) return;

    const baseId = label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || `type-${Date.now()}`;
    let id = baseId;
    let suffix = 2;
    while (attendanceTypes.some((row) => row.id === id)) {
      id = `${baseId}-${suffix++}`;
    }

    const next = [
      ...attendanceTypes,
      {
        id,
        label,
        paid: !!newAttendancePaid,
        allocateHours: !!newAttendanceAllocateHours,
        defaultHours: newAttendanceAllocateHours ? Math.max(0, Number(newAttendanceHours) || 0) : 0,
        requiresApproval: !!newAttendanceApproval,
        showOnSchedule: true,
        ...(/leave/i.test(label) ? { leavePolicy: defaultLeavePolicy({ id, label, paid: !!newAttendancePaid }) } : {}),
      },
    ];

    await persistAttendanceTypes(next);
    setNewAttendanceLabel('');
  };

  const patchAttendanceType = async (id, patch) => {
    const next = attendanceTypes.map((row) =>
      row.id === id
        ? {
            ...row,
            ...patch,
            ...(patch.allocateHours === false ? { defaultHours: 0 } : {}),
          }
        : row
    );
    await persistAttendanceTypes(next);
  };

  const patchLeavePolicy = async (id, patch) => {
    const next = attendanceTypes.map((row) => {
      if (row.id !== id) return row;
      return {
        ...row,
        leavePolicy: {
          ...defaultLeavePolicy(row),
          ...(row.leavePolicy || {}),
          ...patch,
        },
      };
    });
    await persistAttendanceTypes(next);
  };

  const saveLeaveAccrualDay = async (value) => {
    const next = Math.max(1, Math.min(28, Math.round(Number(value) || 1)));
    setLeaveAccrualDayOfMonth(next);
    setLeaveAccrualDaySaveState('saving');
    try {
      await saveLaborSettings({ leaveAccrualDayOfMonth: next });
      setLeaveAccrualDaySaveState('saved');
      setTimeout(() => setLeaveAccrualDaySaveState('idle'), 2200);
    } catch (e) {
      setLeaveAccrualDaySaveState('error');
    }
  };

  const removeAttendanceType = async (id) => {
    await persistAttendanceTypes(attendanceTypes.filter((row) => row.id !== id));
  };

  // Load archived shifts when viewing the Archived tab
  useEffect(() => {
    if (!orgId) return;
    if (activeTab !== 'archived') return;

    let cancelled = false;
    (async () => {
      try {
        setArchivedShiftsLoading(true);
        // 1) legacy shifts (root collection)
        const legacyQ = query(
          collection(db, 'cashup_shifts'),
          where('orgId', '==', orgId),
          where('archived', '==', true),
          limit(200)
        );
        const legacySnap = await getDocs(legacyQ);
        const legacy = legacySnap.docs.map((d) => ({ id: d.id, ...d.data(), __path: ['cashup_shifts', d.id] }));

        // 2) nested shifts (orgs/{orgId}/stores/{storeId}/shifts)
        let nested = [];
        try {
          const storeIds = isAllSites
            ? (await getDocs(collection(db, 'orgs', orgId, 'stores'))).docs.map((d) => d.id)
            : scopedSiteIds;
          const all = [];
          for (const storeId of storeIds) {
            try {
              const sSnap = await getDocs(
                query(
                  collection(db, 'orgs', orgId, 'stores', storeId, 'shifts'),
                  where('archived', '==', true),
                  limit(200)
                )
              );
              sSnap.docs.forEach((sd) => {
                all.push({
                  id: sd.id,
                  ...sd.data(),
                  storeId,
                  __path: ['orgs', orgId, 'stores', storeId, 'shifts', sd.id],
                });
              });
            } catch (e) {}
          }
          nested = all;
        } catch (e) {}

        if (cancelled) return;

        const merged = [...legacy, ...nested].map((s) => {
          const ciSec =
            s.clockInSec ||
            (s.clockIn?.seconds ?? (s.clockIn?.toDate ? Math.floor(s.clockIn.toDate().getTime() / 1000) : null));
          return { ...s, clockInSec: ciSec };
        });

        setArchivedShifts(isAllSites ? merged : merged.filter(rowInScope));
      } catch (e) {
        if (!cancelled) setArchivedShifts([]);
      } finally {
        if (!cancelled) setArchivedShiftsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [orgId, activeTab, isAllSites, scopedSiteIds.join('|')]);

  const restoreShift = async (shift) => {
    if (!shift?.id) return;
    try {
      const ref = Array.isArray(shift.__path) ? doc(db, ...shift.__path) : doc(db, 'cashup_shifts', shift.id);
      await updateDoc(ref, {
        archived: false,
        restoredAt: serverTimestamp(),
        restoredBy: actor?.name || actor?.email || null,
      });

      try {
        await logAudit(db, actor, {
          action: 'RESTORE_SHIFT',
          page: 'Settings',
          entityType: 'shift',
          entityId: shift.id,
          entityName: shift.staffName || shift.staffId || 'shift',
          storeId: shift.storeId || null,
        });
      } catch (e) {}

      // Refresh list
      setArchivedShifts(prev => prev.filter(s => s.id !== shift.id));
    } catch (e) {}
  };
  const isArchivedDoc = (d) => {
    const x = d || {};
    return x.isArchived === true || x.archived === true;
  };


  const [stores, setStores] = useState([]);
  const [roles,  setRoles]  = useState([]);

  // data for Archived tab
  const [managers, setManagers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [sections, setSections] = useState([]);
  const [dashboardSections, setDashboardSections] = useState([]);
  const [shifts, setShifts] = useState([]);

  // Archived shifts (restore-only)
  const [archivedShifts, setArchivedShifts] = useState([]);
  const [archivedShiftsLoading, setArchivedShiftsLoading] = useState(false);
  const [payrollArchivedStaff, setPayrollArchivedStaff] = useState([]);

  // Derived lists
  const activeStores = useMemo(() => {
  return stores.filter((s) => {
    if (!s) return false;
    if (s.isArchived === true) return false;
    if (s.archived === true) return false;
    return true;
  });
}, [stores]);
  const archivedStores = useMemo(() => stores.filter((s) => isArchivedDoc(s)), [stores]);
  const activeRoles = useMemo(() => roles.filter((r) => !isArchivedDoc(r)), [roles]);
  const archivedRoles = useMemo(() => roles.filter((r) => isArchivedDoc(r)), [roles]);
  const activeManagers = useMemo(() => managers.filter((m) => !isArchivedDoc(m)), [managers]);
  const archivedManagers = useMemo(() => managers.filter((m) => isArchivedDoc(m)), [managers]);
  const activeStaff = useMemo(() => staff.filter((u) => !isArchivedDoc(u)), [staff]);
  const archivedStaff = useMemo(() => staff.filter((u) => isArchivedDoc(u)), [staff]);
  const allSections = useMemo(() => [...sections, ...dashboardSections], [sections, dashboardSections]);
  const activeSections = useMemo(() => allSections.filter((x) => !isArchivedDoc(x)), [allSections]);
  const archivedSections = useMemo(() => allSections.filter((x) => isArchivedDoc(x)), [allSections]);

  const storeNameById = useMemo(() => {
    const m = {};
    stores.forEach((st) => {
      if (st?.id) m[st.id] = st.name || st.storeName || "";
    });
    return m;
  }, [stores]);

  const roleNameById = useMemo(() => {
    const m = {};
    roles.forEach((r) => {
      if (r?.id) m[r.id] = r.name || r.roleName || "";
    });
    return m;
  }, [roles]);

  const fmtDateTime = (v) => {
    if (!v) return "—";
    try {
      const d = v?.toDate?.() || (v?.seconds ? new Date(v.seconds * 1000) : new Date(v));
      if (!d || Number.isNaN(d.getTime())) return "—";
      return d.toLocaleString();
    } catch {
      return "—";
    }
  };

  // forms
  const [storeName, setStoreName] = useState('');
  const [roleName,  setRoleName]  = useState('');

  const [confirmArchive, setConfirmArchive] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(null);
  const [editing, setEditing] = useState(null); // {kind:'store'|'role', row}
  const [reasonShift, setReasonShift] = useState(null); // archived shift details modal

  // C8.7 — Settings setup is configuration, not operational realtime data.
  // Wake stores/roles when Settings opens and refresh after intentional writes.
  const loadSettingsSetup = async () => {
    if (!orgId) return;
    const [storesSnap, rolesSnap] = await Promise.all([
      getDocs(query(orgCol(orgId, 'cashup_stores'), orderBy('name'))),
      getDocs(query(orgCol(orgId, 'cashup_roles'), orderBy('name'))),
    ]);
    setStores(storesSnap.docs.map(d => ({ id: d.id, ...(d.data() || {}) })).filter((row) => isAllSites || scopedSiteIds.includes(row.id)));
    setRoles(rolesSnap.docs.map(d => ({ id: d.id, ...(d.data() || {}) })));
  };

  useEffect(() => {
    if (!orgId) return;
    loadSettingsSetup().catch((error) =>
      console.error('[Settings] Failed to load stores/roles', error)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, isAllSites, scopedSiteIds.join('|')]);

  // C8.7 — Archived is historical/admin data. Keep it fully asleep until opened,
  // then perform one fresh snapshot rather than maintaining five listeners.
  const loadArchivedData = async () => {
    if (!orgId) return;
    const [managersSnap, staffSnap, sectionsSnap, dashboardSnap, payrollSnap] = await Promise.all([
      getDocs(collection(db, `orgs/${orgId}/cashup_managers`)),
      getDocs(collection(db, `orgs/${orgId}/cashup_users`)),
      getDocs(collection(db, `orgs/${orgId}/cashup_sections`)),
      getDocs(collection(db, `orgs/${orgId}/staff_dashboard`)),
      getDoc(doc(db, 'orgs', orgId, 'settings', 'payroll')),
    ]);
    setManagers(managersSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(rowInScope));
    setStaff(staffSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(rowInScope));
    setSections(sectionsSnap.docs.map(d => ({ id: d.id, __source: 'cashup_sections', ...d.data() })).filter(rowInScope));
    setDashboardSections(dashboardSnap.docs.map(d => ({ id: d.id, __source: 'staff_dashboard', ...d.data() })).filter(rowInScope));
    setPayrollArchivedStaff((Array.isArray(payrollSnap.data()?.archivedStaff) ? payrollSnap.data().archivedStaff : []).filter(rowInScope));
  };

  useEffect(() => {
    if (!orgId || activeTab !== 'archived') return;
    loadArchivedData().catch((error) =>
      console.error('[Settings] Failed to load archived data', error)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, activeTab, isAllSites, scopedSiteIds.join('|')]);

  const createStore = async () => {
    const name = storeName.trim();
    if (!name) return;
    await addDoc(orgCol(orgId, 'cashup_stores'), {
      orgId,
      name,
      createdAt: serverTimestamp(),
      createdBy: actor?.name || '',
      createdByUid: actor?.uid || '',
      createdByEmail: actor?.email || '',
      updatedAt: serverTimestamp(),
      updatedBy: actor?.name || '',
      updatedByUid: actor?.uid || '',
      updatedByEmail: actor?.email || '',
    });
    setStoreName('');
    await loadSettingsSetup();
  };

  const createRole = async () => {
    const name = roleName.trim();
    if (!name) return;
    await addDoc(orgCol(orgId, 'cashup_roles'), {
      orgId,
      name,
      createdAt: serverTimestamp(),
      createdBy: actor?.name || '',
      createdByUid: actor?.uid || '',
      createdByEmail: actor?.email || '',
      updatedAt: serverTimestamp(),
      updatedBy: actor?.name || '',
      updatedByUid: actor?.uid || '',
      updatedByEmail: actor?.email || '',
    });
    setRoleName('');
    await loadSettingsSetup();
  };

  const applyEdit = async (row, kind, name) => {
    const sub = (kind === 'store') ? 'cashup_stores' : 'cashup_roles';
    await updateDoc(doc(orgCol(orgId, sub), row.id), {
      name: name.trim(),
      updatedAt: serverTimestamp(),
      updatedBy: actor?.name || '',
      updatedByUid: actor?.uid || '',
      updatedByEmail: actor?.email || '',
    });
    setEditing(null);
  };

  const doArchive = async (row, kind) => {
    const sub = (kind === 'store') ? 'cashup_stores' : 'cashup_roles';
    await updateDoc(doc(orgCol(orgId, sub), row.id), {
      isArchived: true,
      archived: true,
      archivedAt: serverTimestamp(),
      archivedBy: actor?.name || '',
      archivedByUid: actor?.uid || '',
      archivedByEmail: actor?.email || '',
      updatedAt: serverTimestamp(),
      updatedBy: actor?.name || '',
      updatedByUid: actor?.uid || '',
      updatedByEmail: actor?.email || '',
    });
    setConfirmArchive(null);
  };

  const restore = async (row, kind) => {
    if (!orgId || !row) return;

    // Payroll archive rows live inside the compact payroll settings document and do
    // not have Firestore document IDs. Handle them before the normal row.id guard.
    if (kind === 'payroll') {
      const rowName = String(row?.name || '').trim();
      if (!rowName) return;

      const settingsRef = doc(db, 'orgs', orgId, 'settings', 'payroll');
      const snap = await getDoc(settingsRef);
      const rows = Array.isArray(snap.data()?.archivedStaff)
        ? snap.data().archivedStaff
        : [];

      // Payroll archiving is unique by staff name, so restore by the same stable key.
      // This also fixes older rows whose archivedAt/reason values were formatted differently.
      const rowNameKey = rowName.toLowerCase();
      const next = rows.filter((x) => String(x?.name || '').trim().toLowerCase() !== rowNameKey);

      await setDoc(
        settingsRef,
        {
          archivedStaff: next,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setPayrollArchivedStaff(next);

      try {
        await logAudit(db, actor, {
          action: 'RESTORE_STAFF_TO_PAYROLL',
          page: 'Settings',
          entityType: 'payroll_staff',
          entityId: rowNameKey,
          entityName: rowName,
        });
      } catch (e) {}

      return;
    }

    if (!row?.id) return;

    const col =
      kind === "store"
        ? "cashup_stores"
        : kind === "role"
        ? "cashup_roles"
        : kind === "manager"
        ? "cashup_managers"
        : kind === "staff"
        ? "cashup_users"
        : kind === "section"
        ? (row.__source === 'staff_dashboard' ? 'staff_dashboard' : 'cashup_sections')
        : 'cashup_sections';

    const ref = doc(db, `orgs/${orgId}/${col}`, row.id);
    await updateDoc(ref, {
      archived: false,
      isArchived: false,
      restoredAt: serverTimestamp(),
      restoredBy: actor?.name || '',
      restoredByUid: actor?.uid || '',
      restoredByEmail: actor?.email || '',
      updatedAt: serverTimestamp(),
      updatedBy: actor?.name || '',
      updatedByUid: actor?.uid || '',
      updatedByEmail: actor?.email || '',
    });
  };

  // Prefer archivedAt, fallback to updatedAt/createdAt for older docs
  const archivedWhen = (row) => row?.archivedAt || row?.updatedAt || row?.createdAt || null;

  if (!orgId) return <div className="p-6 text-white/70">Loading…</div>;

return (
  <div className="p-6 text-white space-y-8">
    {/* Tabs */}
    <div className="flex gap-2">
      <button
        onClick={() => setActiveTab("setup")}
        className={`px-3 py-2 rounded ${
          activeTab === "setup" ? "neo-tab-active" : "bg-white/10"
        }`}
      >
        Store Setup
      </button>

      <button
        onClick={() => setActiveTab("archived")}
        className={`px-3 py-2 rounded ${
          activeTab === "archived" ? "neo-tab-active" : "bg-white/10"
        }`}
      >
        Archived
      </button>
    </div>

    {/* SETUP TAB */}
    {activeTab === "setup" && (
      <>
        {/* PUBLIC HOLIDAYS */}
        <details className="mb-8 rounded border border-white/10">
          <summary className="px-4 py-3 font-semibold cursor-pointer text-xl">
            Public holidays
          </summary>
          <div className="p-3 border-t border-white/10">
            <div className="rounded border border-white/10 p-3 bg-white/5">
              <label className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  checked={publicPayEnabled}
                  onChange={(e) => {
                    const v = e.target.checked;
                    setPublicPayEnabled(v);
                    saveLaborSettings({ publicPayEnabled: v });
                  }}
                />
                <span className="opacity-90">Enable public holiday pay</span>
                <span className="text-xs opacity-60">
                  (If off, public days are treated as normal Mon–Sat)
                </span>
              </label>

              <div className="flex flex-wrap gap-2 items-end">
                <div>
                  <div className="text-xs opacity-70 mb-1">Add public holiday date</div>
                  <input
                  type="date"
                  value={newHoliday}
                  onChange={(e) => setNewHoliday(e.target.value)}
                  onClick={(e) => e.target.showPicker && e.target.showPicker()}
                  className="w-full px-3 py-2 rounded bg-[#0f172a] border border-white/10 cursor-pointer"
                />
                </div>
                <button
                  className="neo-btn neo-btn-accent"
                  onClick={() => {
                    const v = String(newHoliday || "").trim();
                    if (!v) return;
                    const next = Array.from(new Set([...(publicHolidays || []), v])).sort();
                    setPublicHolidays(next);
                    setNewHoliday("");
                    saveLaborSettings({ publicHolidays: next });
                  }}
                >
                  Add
                </button>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {(publicHolidays || []).length === 0 ? (
                  <div className="text-sm opacity-60">No public holidays added yet.</div>
                ) : (
                  publicHolidays.map((d) => (
                    <span
                      key={d}
                      className="inline-flex items-center gap-2 px-3 py-1 rounded bg-white/10 border border-white/10"
                    >
                      <span>{d}</span>
                      <button
                        className="text-xs opacity-80 hover:opacity-100"
                        onClick={() => {
                          const next = (publicHolidays || []).filter((x) => x !== d);
                          setPublicHolidays(next);
                          saveLaborSettings({ publicHolidays: next });
                        }}
                      >
                        ✕
                      </button>
                    </span>
                  ))
                )}
              </div>
            </div>
          </div>
        </details>

        {/* LEAVE & ATTENDANCE TYPES */}
        <details className="mb-8 rounded border border-white/10">
          <summary className="px-4 py-3 font-semibold cursor-pointer text-xl">
            Leave & attendance types
          </summary>
          <div className="p-3 border-t border-white/10 space-y-4">
            <div className="rounded border border-cyan-400/20 bg-cyan-400/5 p-3">
              <div className="font-semibold">Schedule source of truth</div>
              <div className="mt-1 text-sm opacity-70">
                Configure the options managers can place on the monthly schedule. Schedule Intelligence reads these settings automatically.
              </div>
              <div className="mt-2 text-xs opacity-60">
                Payroll rules remain in Payroll. These settings only describe the schedule status and whether the roster should allocate planning hours.
              </div>
            </div>

            <div className="rounded border border-cyan-400/20 bg-cyan-400/5 p-3">
              <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div>
                  <div className="font-semibold text-cyan-100">Organisation leave accrual date</div>
                  <div className="mt-1 max-w-2xl text-sm opacity-70">
                    Neo credits monthly leave on this day for the whole organisation. The 1st is the default so balances are current from the start of the payroll month.
                  </div>
                </div>
                <label className="min-w-[220px]">
                  <span className="text-xs opacity-60">Default monthly accrual day</span>
                  <select
                    value={leaveAccrualDayOfMonth}
                    onChange={(e) => saveLeaveAccrualDay(e.target.value)}
                    className="mt-1 w-full rounded border border-white/10 bg-[#0f172a] px-3 py-2"
                  >
                    {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                      <option key={day} value={day}>{day}{day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th'} of each month</option>
                    ))}
                  </select>
                  <div className={`mt-1 text-xs ${leaveAccrualDaySaveState === 'error' ? 'text-rose-300' : 'opacity-50'}`}>
                    {leaveAccrualDaySaveState === 'saving' ? 'Saving…' : leaveAccrualDaySaveState === 'saved' ? 'Saved' : leaveAccrualDaySaveState === 'error' ? 'Could not save accrual date.' : 'Leave types can override this below.'}
                  </div>
                </label>
              </div>
            </div>

            <div className="space-y-2">
              {attendanceTypes.map((row) => (
                <div key={row.id} className="rounded border border-white/10 bg-white/5 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="font-semibold">{row.label}</div>
                      <div className="mt-1 text-xs opacity-60">
                        {row.paid ? 'Paid' : 'Unpaid'} · {row.allocateHours ? `${Number(row.defaultHours || 0)}h allocated` : 'No hours allocated'} · {row.requiresApproval ? 'Approval required' : 'No approval required'}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttendanceType(row.id)}
                      className="rounded border border-rose-400/20 px-3 py-1.5 text-xs text-rose-200 hover:bg-rose-400/10"
                    >
                      Remove
                    </button>
                  </div>

                  <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
                    <label className="flex items-center gap-2 rounded border border-white/10 px-3 py-2">
                      <input
                        type="checkbox"
                        checked={row.paid !== false}
                        onChange={(e) => patchAttendanceType(row.id, { paid: e.target.checked })}
                      />
                      <span className="text-sm">Paid</span>
                    </label>

                    <label className="flex items-center gap-2 rounded border border-white/10 px-3 py-2">
                      <input
                        type="checkbox"
                        checked={row.allocateHours === true}
                        onChange={(e) => patchAttendanceType(row.id, { allocateHours: e.target.checked })}
                      />
                      <span className="text-sm">Allocate hours</span>
                    </label>

                    <label className="block">
                      <span className="text-xs opacity-60">Default hours</span>
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        disabled={!row.allocateHours}
                        value={row.allocateHours ? Number(row.defaultHours || 0) : 0}
                        onChange={(e) => patchAttendanceType(row.id, { defaultHours: Math.max(0, Number(e.target.value) || 0) })}
                        className="mt-1 w-full rounded border border-white/10 bg-[#0f172a] px-3 py-2 disabled:opacity-40"
                      />
                    </label>

                    <label className="flex items-center gap-2 rounded border border-white/10 px-3 py-2">
                      <input
                        type="checkbox"
                        checked={row.requiresApproval === true}
                        onChange={(e) => patchAttendanceType(row.id, { requiresApproval: e.target.checked })}
                      />
                      <span className="text-sm">Manager approval</span>
                    </label>

                    <label className="flex items-center gap-2 rounded border border-white/10 px-3 py-2">
                      <input
                        type="checkbox"
                        checked={row.showOnSchedule !== false}
                        onChange={(e) => patchAttendanceType(row.id, { showOnSchedule: e.target.checked })}
                      />
                      <span className="text-sm">Show on schedule</span>
                    </label>
                  </div>

                  {isLeaveAttendanceType(row) && (
                    <details className="mt-3 rounded border border-violet-400/20 bg-violet-400/5">
                      <summary className="cursor-pointer px-3 py-2 text-sm font-semibold text-violet-100">
                        Leave policy
                        <span className="ml-2 text-xs font-normal text-white/45">
                          {row.leavePolicy?.enabled === false ? 'Accrual disabled' : `${Number(row.leavePolicy?.accrualAmountDays || 0)} day(s) · ${row.leavePolicy?.accrualFrequency || 'none'}`}
                        </span>
                      </summary>
                      <div className="grid grid-cols-1 gap-3 border-t border-white/10 p-3 md:grid-cols-2 xl:grid-cols-4">
                        <label className="flex items-center gap-2 rounded border border-white/10 px-3 py-2">
                          <input type="checkbox" checked={row.leavePolicy?.enabled !== false} onChange={(e) => patchLeavePolicy(row.id, { enabled: e.target.checked })} />
                          <span className="text-sm">Accrue entitlement</span>
                        </label>

                        <label>
                          <span className="text-xs opacity-60">Accrual days</span>
                          <input type="number" min="0" step="0.25" disabled={row.leavePolicy?.enabled === false} value={Number(row.leavePolicy?.accrualAmountDays || 0)} onChange={(e) => patchLeavePolicy(row.id, { accrualAmountDays: Math.max(0, Number(e.target.value) || 0) })} className="mt-1 w-full rounded border border-white/10 bg-[#0f172a] px-3 py-2 disabled:opacity-40" />
                        </label>

                        <label>
                          <span className="text-xs opacity-60">Accrual frequency</span>
                          <select disabled={row.leavePolicy?.enabled === false} value={row.leavePolicy?.accrualFrequency || 'none'} onChange={(e) => patchLeavePolicy(row.id, { accrualFrequency: e.target.value })} className="mt-1 w-full rounded border border-white/10 bg-[#0f172a] px-3 py-2 disabled:opacity-40">
                            <option value="none">No automatic accrual</option>
                            <option value="monthly">Every month</option>
                            <option value="yearly">Every cycle / year</option>
                          </select>
                        </label>

                        <label>
                          <span className="text-xs opacity-60">Accrual day override</span>
                          <select
                            disabled={row.leavePolicy?.enabled === false || row.leavePolicy?.accrualFrequency === 'none'}
                            value={Math.max(0, Math.min(28, Number(row.leavePolicy?.accrualDayOfMonth || 0)))}
                            onChange={(e) => patchLeavePolicy(row.id, { accrualDayOfMonth: Math.max(0, Math.min(28, Number(e.target.value) || 0)) })}
                            className="mt-1 w-full rounded border border-white/10 bg-[#0f172a] px-3 py-2 disabled:opacity-40"
                          >
                            <option value={0}>Organisation default ({leaveAccrualDayOfMonth}{leaveAccrualDayOfMonth === 1 ? 'st' : leaveAccrualDayOfMonth === 2 ? 'nd' : leaveAccrualDayOfMonth === 3 ? 'rd' : 'th'})</option>
                            {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                              <option key={day} value={day}>{day}{day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th'}</option>
                            ))}
                          </select>
                        </label>

                        <label>
                          <span className="text-xs opacity-60">Entitlement cycle (months)</span>
                          <input type="number" min="1" max="60" value={Math.max(1, Number(row.leavePolicy?.cycleMonths || 12))} onChange={(e) => patchLeavePolicy(row.id, { cycleMonths: Math.max(1, Number(e.target.value) || 12) })} className="mt-1 w-full rounded border border-white/10 bg-[#0f172a] px-3 py-2" />
                        </label>

                        <label>
                          <span className="text-xs opacity-60">Maximum balance (0 = unlimited)</span>
                          <input type="number" min="0" step="0.5" value={Number(row.leavePolicy?.maximumBalanceDays || 0)} onChange={(e) => patchLeavePolicy(row.id, { maximumBalanceDays: Math.max(0, Number(e.target.value) || 0) })} className="mt-1 w-full rounded border border-white/10 bg-[#0f172a] px-3 py-2" />
                        </label>

                        <label className="flex items-center gap-2 rounded border border-white/10 px-3 py-2">
                          <input type="checkbox" checked={row.leavePolicy?.carryOver === true} onChange={(e) => patchLeavePolicy(row.id, { carryOver: e.target.checked })} />
                          <span className="text-sm">Carry over balance</span>
                        </label>

                        <label>
                          <span className="text-xs opacity-60">Carry-over limit (0 = unlimited)</span>
                          <input type="number" min="0" step="0.5" disabled={row.leavePolicy?.carryOver !== true} value={Number(row.leavePolicy?.carryOverLimitDays || 0)} onChange={(e) => patchLeavePolicy(row.id, { carryOverLimitDays: Math.max(0, Number(e.target.value) || 0) })} className="mt-1 w-full rounded border border-white/10 bg-[#0f172a] px-3 py-2 disabled:opacity-40" />
                        </label>

                        <label className="flex items-center gap-2 rounded border border-white/10 px-3 py-2">
                          <input type="checkbox" checked={row.leavePolicy?.resetAtCycleEnd === true} onChange={(e) => patchLeavePolicy(row.id, { resetAtCycleEnd: e.target.checked })} />
                          <span className="text-sm">Reset at cycle end</span>
                        </label>

                        <label className="flex items-center gap-2 rounded border border-white/10 px-3 py-2">
                          <input type="checkbox" checked={row.leavePolicy?.evidenceRequired === true} onChange={(e) => patchLeavePolicy(row.id, { evidenceRequired: e.target.checked })} />
                          <span className="text-sm">Supporting evidence</span>
                        </label>

                        <label>
                          <span className="text-xs opacity-60">Evidence after consecutive days</span>
                          <input type="number" min="0" step="1" disabled={row.leavePolicy?.evidenceRequired !== true} value={Number(row.leavePolicy?.evidenceAfterDays || 0)} onChange={(e) => patchLeavePolicy(row.id, { evidenceAfterDays: Math.max(0, Math.round(Number(e.target.value) || 0)) })} className="mt-1 w-full rounded border border-white/10 bg-[#0f172a] px-3 py-2 disabled:opacity-40" />
                        </label>

                        <label className="flex items-center gap-2 rounded border border-white/10 px-3 py-2 md:col-span-2">
                          <input type="checkbox" checked={row.leavePolicy?.blockIfInsufficient === true} onChange={(e) => patchLeavePolicy(row.id, { blockIfInsufficient: e.target.checked })} />
                          <span className="text-sm">Block requests when available balance is insufficient</span>
                        </label>
                      </div>
                      <div className="border-t border-white/10 px-3 py-2 text-xs text-white/45">
                        LR10.1 uses the organisation accrual date above (1st by default). A leave type can override that day without changing opening-balance dates.
                      </div>
                    </details>
                  )}
                </div>
              ))}
            </div>

            <div className="rounded border border-white/10 bg-black/20 p-3">
              <div className="font-semibold">Add attendance type</div>
              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-6">
                <label className="xl:col-span-2">
                  <span className="text-xs opacity-60">Name</span>
                  <input
                    value={newAttendanceLabel}
                    onChange={(e) => setNewAttendanceLabel(e.target.value)}
                    placeholder="e.g. Family Responsibility Leave"
                    className="mt-1 w-full rounded border border-white/10 bg-[#0f172a] px-3 py-2"
                  />
                </label>

                <label className="flex items-center gap-2 rounded border border-white/10 px-3 py-2 mt-5">
                  <input type="checkbox" checked={newAttendancePaid} onChange={(e) => setNewAttendancePaid(e.target.checked)} />
                  <span className="text-sm">Paid</span>
                </label>

                <label className="flex items-center gap-2 rounded border border-white/10 px-3 py-2 mt-5">
                  <input type="checkbox" checked={newAttendanceAllocateHours} onChange={(e) => setNewAttendanceAllocateHours(e.target.checked)} />
                  <span className="text-sm">Allocate hours</span>
                </label>

                <label>
                  <span className="text-xs opacity-60">Hours</span>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    disabled={!newAttendanceAllocateHours}
                    value={newAttendanceAllocateHours ? newAttendanceHours : 0}
                    onChange={(e) => setNewAttendanceHours(Math.max(0, Number(e.target.value) || 0))}
                    className="mt-1 w-full rounded border border-white/10 bg-[#0f172a] px-3 py-2 disabled:opacity-40"
                  />
                </label>

                <button
                  type="button"
                  onClick={addAttendanceType}
                  disabled={!String(newAttendanceLabel || '').trim() || attendanceSaveState === 'saving'}
                  className="neo-btn neo-btn-accent mt-5 disabled:opacity-40"
                >
                  {attendanceSaveState === 'saving' ? 'Saving…' : 'Add type'}
                </button>
              </div>

              <label className="mt-3 flex items-center gap-2 text-sm opacity-80">
                <input type="checkbox" checked={newAttendanceApproval} onChange={(e) => setNewAttendanceApproval(e.target.checked)} />
                Manager approval required for this type
              </label>

              {attendanceSaveState === 'saved' ? (
                <div className="mt-2 text-xs text-emerald-300">Attendance settings saved.</div>
              ) : attendanceSaveState === 'error' ? (
                <div className="mt-2 text-xs text-rose-300">Could not save attendance settings.</div>
              ) : null}
            </div>
          </div>
        </details>

        {/* AUTO CLOCK-OUT */}
        <details className="mb-8 rounded border border-white/10">
          <summary className="px-4 py-3 font-semibold cursor-pointer text-xl">
            Auto clock-out
          </summary>
          <div className="p-3 border-t border-white/10">
            <div className="rounded border border-white/10 p-3 bg-white/5 space-y-4">
              <div className="rounded border border-white/10 p-3 bg-black/20">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-medium opacity-90">
                      Force auto clock-out (hours)
                    </div>
                    <div className="mt-1 text-xs text-cyan-200/80">
                      Shared store policy · Neo Admin and the Neo App use the same setting.
                    </div>
                  </div>
                  <span className="rounded-full border border-cyan-400/30 bg-cyan-400/10 px-2.5 py-1 text-xs font-semibold text-cyan-200">
                    18–24 hours
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2 items-center">
                  <input
                    type="number"
                    min="18"
                    max="24"
                    value={abandonedShiftHours}
                    onChange={(e) => {
                      setAbandonedShiftHours(e.target.value);
                      if (thresholdSaveState !== 'idle') setThresholdSaveState('idle');
                    }}
                    className="w-28 px-3 py-2 rounded bg-[#0f172a] border border-white/10"
                  />
                  <button
                    className="neo-btn neo-btn-accent"
                    onClick={async () => {
                      const safeHours = Math.max(18, Math.min(24, Math.round(Number(abandonedShiftHours) || 18)));
                      setAbandonedShiftHours(safeHours);
                      setThresholdSaveState('saving');
                      try {
                        await saveLaborSettings({
                          abandonedShiftHours: safeHours,
                          forceAutoClockOutHours: safeHours,
                          autoClockOutMode: 'force-close',
                        });

                        // Keep each store/device policy aligned with the same value used by Neo Admin.
                        // Neo App installations are store-bound, so writing the shared setting onto the
                        // active store documents lets both surfaces consume the same source of truth.
                        await Promise.all(
                          activeStores.map((storeRow) =>
                            setDoc(
                              doc(db, 'orgs', orgId, 'cashup_stores', storeRow.id),
                              {
                                abandonedShiftHours: safeHours,
                                forceAutoClockOutHours: safeHours,
                                autoClockOutMode: 'force-close',
                                autoClockOutUpdatedAt: serverTimestamp(),
                              },
                              { merge: true }
                            )
                          )
                        );

                        setThresholdSaveState('saved');
                      } catch (e) {
                        setThresholdSaveState('error');
                      }
                    }}
                  >
                    Save auto clock-out
                  </button>
                  {thresholdSaveState === 'saving' ? (
                    <span className="text-sm text-white/70">Saving...</span>
                  ) : thresholdSaveState === 'saved' ? (
                    <span className="text-sm text-emerald-400">Auto clock-out saved!</span>
                  ) : thresholdSaveState === 'error' ? (
                    <span className="text-sm text-rose-400">Could not save auto clock-out.</span>
                  ) : null}
                </div>
                <div className="text-sm opacity-70 mt-3">
                  This is a safety cut-off, not a Labor-hours cap. If a shift remains open for this many hours,
                  Neo must close the shift. Labor no longer clips worked hours to this value; Schedule Intelligence
                  controls scheduled time and overtime approval.
                </div>
              </div>
            </div>
          </div>
        </details>

        {/* P1.1 — Photo clock-in policy. Load only while explicitly expanded. */}
        <details className="mb-8 rounded border border-white/10" onToggle={(event) => setPhotoSettingsExpanded(event.currentTarget.open)}>
          <summary className="px-4 py-3 font-semibold cursor-pointer text-xl">Photo Clock-In Verification</summary>
          {photoSettingsExpanded ? (
            <div className="p-3 border-t border-white/10">
              <PhotoAttendanceSettings orgId={orgId} stores={activeStores} />
            </div>
          ) : null}
        </details>

        {/* STORES */}
        <details className="mb-8 rounded border border-white/10">
          <summary className="px-4 py-3 font-semibold cursor-pointer text-xl">
            Stores
          </summary>
          <div className="p-3 border-t border-white/10">
            <div className="flex gap-2 mb-3">
              <input
                className="bg-black/40 border border-white/10 rounded px-3 py-2"
                placeholder="New store name"
                value={storeName}
                onChange={(e) => setStoreName(e.target.value)}
              />
              <button onClick={createStore} className="neo-btn neo-btn-accent">
                Create Store
              </button>
            </div>

            <div className="rounded border border-white/10 neo-mobile-table-scroll overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/10">
                  <tr>
                    <th className="px-3 py-2 text-left">Store Name</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {activeStores.length === 0 ? (
                    <tr>
                      <td colSpan="2" className="px-3 py-3 text-center text-white/60">
                        No stores.
                      </td>
                    </tr>
                  ) : (
                    activeStores.map((s) => (
                      <tr key={s.id} className="border-t border-white/10">
                        <td className="px-3 py-2">{s.name}</td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => setEditing({ kind: "store", row: s })}
                            className="px-3 py-1 rounded bg-slate-700 hover:bg-slate-600"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => setConfirmArchive({ kind: "store", row: s })}
                            className="ml-2 px-3 py-1 rounded bg-rose-700 hover:bg-rose-600"
                          >
                            Archive
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </details>

        {/* ROLES */}
        <details className="mb-8 rounded border border-white/10">
          <summary className="px-4 py-3 font-semibold cursor-pointer text-xl">
            Roles
          </summary>
          <div className="p-3 border-t border-white/10">
            <div className="flex gap-2 mb-3">
              <input
                className="bg-black/40 border border-white/10 rounded px-3 py-2"
                placeholder="New role name"
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
              />
              <button onClick={createRole} className="neo-btn neo-btn-accent">
                Create Role
              </button>
            </div>

            <div className="rounded border border-white/10 neo-mobile-table-scroll overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/10">
                  <tr>
                    <th className="px-3 py-2 text-left">Role</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {activeRoles.length === 0 ? (
                    <tr>
                      <td colSpan="2" className="px-3 py-3 text-center text-white/60">
                        No roles.
                      </td>
                    </tr>
                  ) : (
                    activeRoles.map((r) => (
                      <tr key={r.id} className="border-t border-white/10">
                        <td className="px-3 py-2">{r.name}</td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => setEditing({ kind: "role", row: r })}
                            className="px-3 py-1 rounded bg-slate-700 hover:bg-slate-600"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => setConfirmArchive({ kind: "role", row: r })}
                            className="ml-2 px-3 py-1 rounded bg-rose-700 hover:bg-rose-600"
                          >
                            Archive
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </details>
      </>
    )}
            
      {/* ARCHIVED TAB */}
      {activeTab === "archived" && (
        <div className="space-y-6">

          <details className="rounded border border-white/10">
            <summary className="px-3 py-2 font-semibold cursor-pointer">Archived Stores</summary>
            <div className="p-3">
              <div className="rounded border border-white/10 neo-mobile-table-scroll overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/10">
                    <tr>
                      <th className="px-3 py-2 text-left">Store Name</th>
                      <th className="px-3 py-2 text-left">Archived</th>
                      <th className="px-3 py-2 text-left">Archived By</th>
                      <th className="px-3 py-2 text-left">Reasons</th>
                      <th className="px-3 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stores.filter(s => s.isArchived).length === 0 ? (
                      <tr><td colSpan="4" className="px-3 py-3 text-center text-white/60">No archived stores.</td></tr>
                    ) : stores.filter(s => s.isArchived).map(s => (
                      <tr key={s.id} className="border-t border-white/10">
                        <td className="px-3 py-2">{s.name}</td>
                        <td className="px-3 py-2">{fmtDateTime(archivedWhen(s))}</td>
                        <td className="px-3 py-2"> {s.archivedBy || '—'}</td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => setReasonShift(s)}
                            className="px-3 py-1 rounded bg-slate-700 hover:bg-slate-600 disabled:opacity-40"
                            disabled={!((s.reason || '').trim())}
                            title={!((s.reason || '').trim()) ? 'No reason saved for this shift.' : 'Open reason/details'}
                          >Open</button>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => setConfirmRestore({ kind: "store", row: s })}
                            className="px-3 py-1 rounded bg-emerald-700 hover:bg-emerald-600"
                          >Restore</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </details>

          <details className="rounded border border-white/10">
            <summary className="px-3 py-2 font-semibold cursor-pointer">Archived Roles</summary>
            <div className="p-3">
              <div className="rounded border border-white/10 neo-mobile-table-scroll overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/10">
                    <tr>
                      <th className="px-3 py-2 text-left">Role</th>
                      <th className="px-3 py-2 text-left">Archived</th>
                      <th className="px-3 py-2 text-left">Archived By</th>
                      <th className="px-3 py-2 text-left">Reasons</th>
                      <th className="px-3 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roles.filter(r => r.isArchived).length === 0 ? (
                      <tr><td colSpan="4" className="px-3 py-3 text-center text-white/60">No archived roles.</td></tr>
                    ) : roles.filter(r => r.isArchived).map(r => (
                      <tr key={r.id} className="border-t border-white/10">
                        <td className="px-3 py-2">{r.name}</td>
                        <td className="px-3 py-2">{fmtDateTime(archivedWhen(r))}</td>
                        <td className="px-3 py-2">{r.archivedBy || '—'}</td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => setReasonShift(r)}
                            className="px-3 py-1 rounded bg-slate-700 hover:bg-slate-600 disabled:opacity-40"
                            disabled={!((r.reason || '').trim())}
                            title={!((r.reason || '').trim()) ? 'No reason saved.' : 'Open reason/details'}
                          >Open</button>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => setConfirmRestore({ kind: "role", row: r })}
                            className="px-3 py-1 rounded bg-emerald-700 hover:bg-emerald-600"
                          >Restore</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </details>

          <details className="rounded border border-white/10">
            <summary className="px-3 py-2 font-semibold cursor-pointer">Archived Managers</summary>
            <div className="p-3">
              <div className="rounded border border-white/10 neo-mobile-table-scroll overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/10">
                    <tr>
                      <th className="px-3 py-2 text-left">Name</th>
                      <th className="px-3 py-2 text-left">Store</th>
                      <th className="px-3 py-2 text-left">Archived</th>
                      <th className="px-3 py-2 text-left">Archived By</th>
                      <th className="px-3 py-2 text-left">Reasons</th>
                      <th className="px-3 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {managers.filter(m => m.isArchived).length === 0 ? (
                      <tr><td colSpan="5" className="px-3 py-3 text-center text-white/60">No archived managers.</td></tr>
                    ) : managers.filter(m => m.isArchived).map(m => (
                      <tr key={m.id} className="border-t border-white/10">
                        <td className="px-3 py-2">{m.name}</td>
                        <td className="px-3 py-2">{stores.find(s => s.id === m.storeId)?.name || m.storeName || m.storeId || "—"}</td>
                        <td className="px-3 py-2">{fmtDateTime(m.archivedAt)}</td>
                        <td className="px-3 py-2">{m.archivedBy || '—'}</td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => setReasonShift(m)}
                            className="px-3 py-1 rounded bg-slate-700 hover:bg-slate-600 disabled:opacity-40"
                            disabled={!((m.reason || '').trim())}
                            title={!((m.reason || '').trim()) ? 'No reason saved.' : 'Open reason/details'}
                          >Open</button>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => setConfirmRestore({ kind: "manager", row: m })}
                            className="px-3 py-1 rounded bg-emerald-700 hover:bg-emerald-600"
                          >Restore</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </details>

          <details className="rounded border border-white/10">
            <summary className="px-3 py-2 font-semibold cursor-pointer">Archived Staff</summary>
            <div className="p-3">
              <div className="rounded border border-white/10 neo-mobile-table-scroll overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/10">
                    <tr>
                      <th className="px-3 py-2 text-left">Name</th>
                      <th className="px-3 py-2 text-left">Role</th>
                      <th className="px-3 py-2 text-left">Store</th>
                      <th className="px-3 py-2 text-left">Archived</th>
                      <th className="px-3 py-2 text-left">Archived By</th>
                      <th className="px-3 py-2 text-left">Reasons</th>
                      <th className="px-3 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {archivedStaff.length === 0 ? (
                      <tr><td colSpan="6" className="px-3 py-3 text-center text-white/60">No archived staff.</td></tr>
                    ) : archivedStaff.map(x => (
                      <tr key={x.id} className="border-t border-white/10">
                        <td className="px-3 py-2">{x.name}</td>
                        <td className="px-3 py-2">{x.roleName || roleNameById[x.roleId] || "—"}</td>
                        <td className="px-3 py-2">{x.storeName || storeNameById[x.storeId] || "—"}</td>
                        <td className="px-3 py-2">{x.archivedAt?.toDate ? x.archivedAt.toDate().toLocaleString() : (x.archivedAt ? new Date(x.archivedAt).toLocaleString() : "—")}</td>
                        <td className="px-3 py-2">{x.archivedBy || '—'}</td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => setReasonShift(x)}
                            className="px-3 py-1 rounded bg-slate-700 hover:bg-slate-600 disabled:opacity-40"
                            disabled={!((x.reason || '').trim())}
                            title={!((x.reason || '').trim()) ? 'No reason saved.' : 'Open reason/details'}
                          >Open</button>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => setConfirmRestore({ kind: "staff", row: x })}
                            className="px-3 py-1 rounded bg-emerald-700 hover:bg-emerald-600"
                          >Restore</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </details>

          <details className="rounded border border-white/10">
            <summary className="px-3 py-2 font-semibold cursor-pointer">Archived Shifts</summary>
            <div className="p-3">
              <div className="rounded border border-white/10 neo-mobile-table-scroll overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/10">
                    <tr>
                      <th className="px-3 py-2 text-left">Name</th>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-left">In</th>
                      <th className="px-3 py-2 text-left">Out</th>
                      <th className="px-3 py-2 text-left">Store</th>
                      <th className="px-3 py-2 text-left">Archived</th>
                      <th className="px-3 py-2 text-left">Archived By</th>
                      <th className="px-3 py-2 text-left">Reasons</th>
                      <th className="px-3 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {archivedShifts.length === 0 ? (
                      <tr><td colSpan="9" className="px-3 py-3 text-center text-white/60">No archived shifts.</td></tr>
                    ) : archivedShifts
                      .sort((a,b) => (b.clockInSec || 0) - (a.clockInSec || 0))
                      .slice(0, 200)
                      .map(s => (
                      <tr key={s.id} className="border-t border-white/10">
                        <td className="px-3 py-2">{s.name || '—'}</td>
                        <td className="px-3 py-2">{s.date || (s.clockIn?.toDate ? s.clockIn.toDate().toLocaleDateString() : '—')}</td>
                        <td className="px-3 py-2">{s.clockIn?.toDate ? s.clockIn.toDate().toLocaleTimeString() : (s.clockInSec ? new Date(s.clockInSec*1000).toLocaleTimeString() : '—')}</td>
                        <td className="px-3 py-2">{s.clockOut?.toDate ? s.clockOut.toDate().toLocaleTimeString() : (s.clockOutSec ? new Date(s.clockOutSec*1000).toLocaleTimeString() : '—')}</td>
                        <td className="px-3 py-2">{s.storeName || storeNameById[s.storeId] || '—'}</td>
                        <td className="px-3 py-2">{s.archivedAt?.toDate ? s.archivedAt.toDate().toLocaleString() : (s.archivedAt ? new Date(s.archivedAt).toLocaleString() : '—')}</td>
                        <td className="px-3 py-2"> {s.archivedBy || '—'}</td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => setReasonShift(s)}
                            className="px-3 py-1 rounded bg-slate-700 hover:bg-slate-600 disabled:opacity-40"
                            disabled={!((s.reason || '').trim())}
                            title={!((s.reason || '').trim()) ? 'No reason saved for this shift.' : 'Open reason/details'}
                          >Open</button>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => setConfirmRestore({ kind: 'shift', row: s })}
                            className="px-3 py-1 rounded bg-emerald-700 hover:bg-emerald-600"
                          >Restore</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-xs text-white/50 mt-2">Showing up to the latest 200 archived shifts.</div>
            </div>
          </details>

          <details className="rounded border border-white/10">
            <summary className="px-3 py-2 font-semibold cursor-pointer">Archived Payroll</summary>
            <div className="p-3">
              <div className="rounded border border-white/10 neo-mobile-table-scroll overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/10">
                    <tr>
                      <th className="px-3 py-2 text-left">Name</th>
                      <th className="px-3 py-2 text-left">Archived</th>
                      <th className="px-3 py-2 text-left">Archived By</th>
                      <th className="px-3 py-2 text-left">Reasons</th>
                      <th className="px-3 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payrollArchivedStaff.length === 0 ? (
                      <tr><td colSpan="5" className="px-3 py-3 text-center text-white/60">No payroll-archived staff.</td></tr>
                    ) : payrollArchivedStaff.map((s, idx) => (
                      <tr key={`${s.name || 'staff'}-${idx}`} className="border-t border-white/10">
                        <td className="px-3 py-2">{s.name || '—'}</td>
                        <td className="px-3 py-2">{fmtDateTime(s.archivedAt)}</td>
                        <td className="px-3 py-2">{s.archivedBy || '—'}</td>
                        <td className="px-3 py-2 whitespace-pre-wrap">{(s.reason || '').trim() || '—'}</td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => setConfirmRestore({ kind: 'payroll', row: s })}
                            className="px-3 py-1 rounded bg-emerald-700 hover:bg-emerald-600"
                          >Restore</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </details>

          <details className="rounded border border-white/10">
            <summary className="px-3 py-2 font-semibold cursor-pointer">Archived Sections</summary>
            <div className="p-3">
              <div className="rounded border border-white/10 neo-mobile-table-scroll overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/10">
                    <tr>
                      <th className="px-3 py-2 text-left">Section</th>
                      <th className="px-3 py-2 text-left">Store</th>
                      <th className="px-3 py-2 text-left">Archived</th>
                      <th className="px-3 py-2 text-left">Archived By</th>
                      <th className="px-3 py-2 text-left">Reasons</th>
                      <th className="px-3 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {archivedSections.length === 0 ? (
                      <tr><td colSpan="5" className="px-3 py-3 text-center text-white/60">No archived sections.</td></tr>
                    ) : archivedSections.map(s => (
                      <tr key={s.id} className="border-t border-white/10">
                        <td className="px-3 py-2">{s.name}</td>
                        <td className="px-3 py-2">{s.storeName || "—"}</td>
                        <td className="px-3 py-2">{fmtDateTime(archivedWhen(s))}</td>
                        <td className="px-3 py-2"> {s.archivedBy || '—'}</td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => setReasonShift(s)}
                            className="px-3 py-1 rounded bg-slate-700 hover:bg-slate-600 disabled:opacity-40"
                            disabled={!((s.reason || '').trim())}
                            title={!((s.reason || '').trim()) ? 'No reason saved for this shift.' : 'Open reason/details'}
                          >Open</button>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => setConfirmRestore({ kind: "section", row: s })}
                            className="px-3 py-1 rounded bg-emerald-700 hover:bg-emerald-600"
                          >Restore</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </details>

        </div>
      )}
    {/* edit prompt */}
    {editing && (
      <Prompt
        open={true}
        title={`Edit ${editing.kind === "store" ? "Store" : "Role"}`}
        label="Name"
        initial={editing.row.name || ""}
        onCancel={() => setEditing(null)}
        onOk={(v) => applyEdit(editing.row, editing.kind, v)}
      />
    )}

    {/* archive confirm */}
    <Confirm
      open={!!confirmArchive}
      title={`Archive ${confirmArchive?.kind === "store" ? "Store" : "Role"}`}
      message={`Archive “${confirmArchive?.row?.name || ""}”?`}
      onCancel={() => setConfirmArchive(null)}
      onOk={() => doArchive(confirmArchive.row, confirmArchive.kind)}
      okLabel="Archive"
    />

    
    {/* archived reason/details */}
    {reasonShift ? (
      <Modal title="Archived Details" onClose={() => setReasonShift(null)}>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-white/60">Employee</div>
              <div className="font-medium">{reasonShift?.name || '—'}</div>
            </div>
            <div>
              <div className="text-white/60">Store</div>
              <div className="font-medium">{reasonShift?.storeName || storeNameById[reasonShift?.storeId] || '—'}</div>
            </div>
            <div>
              <div className="text-white/60">Date</div>
              <div className="font-medium">{reasonShift?.date || (reasonShift?.clockIn?.toDate ? reasonShift.clockIn.toDate().toLocaleDateString() : '—')}</div>
            </div>
            <div>
              <div className="text-white/60">Archived</div>
              <div className="font-medium">
                {reasonShift?.archivedAt?.toDate ? reasonShift.archivedAt.toDate().toLocaleString() : (reasonShift?.archivedAt ? new Date(reasonShift.archivedAt).toLocaleString() : '—')}
              </div>
              <div className="text-white/60">{reasonShift?.archivedBy ? `By: ${reasonShift.archivedBy}` : ''}</div>
            </div>
            <div>
              <div className="text-white/60">Last edited</div>
              <div className="font-medium">
                {reasonShift?.editedAt?.toDate ? reasonShift.editedAt.toDate().toLocaleString() : (reasonShift?.editedAt ? new Date(reasonShift.editedAt).toLocaleString() : '—')}
              </div>
              <div className="text-white/60">{reasonShift?.editedBy ? `By: ${reasonShift.editedBy}` : ''}</div>
            </div>
          </div>

          <div className="rounded bg-white/5 border border-white/10 p-3">
            <div className="text-white/70 mb-1">Reason</div>
            <div className="whitespace-pre-wrap">{(reasonShift?.reason || '').trim() || '—'}</div>
          </div>

          <div className="rounded bg-white/5 border border-white/10 p-3">
            <div className="text-white/70 mb-2">Shift details</div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <div className="text-white/60">Clock in</div>
                <div className="font-medium">
                  {reasonShift?.clockIn?.toDate ? reasonShift.clockIn.toDate().toLocaleTimeString() : (reasonShift?.clockInSec ? new Date(reasonShift.clockInSec*1000).toLocaleTimeString() : '—')}
                </div>
                {reasonShift?.oldClockIn ? (
                  <div className="text-white/50 text-xs">Old: {reasonShift.oldClockIn.toDate ? reasonShift.oldClockIn.toDate().toLocaleTimeString() : '—'}</div>
                ) : null}
              </div>
              <div>
                <div className="text-white/60">Clock out</div>
                <div className="font-medium">
                  {reasonShift?.clockOut?.toDate ? reasonShift.clockOut.toDate().toLocaleTimeString() : (reasonShift?.clockOutSec ? new Date(reasonShift.clockOutSec*1000).toLocaleTimeString() : '—')}
                </div>
                {reasonShift?.oldClockOut ? (
                  <div className="text-white/50 text-xs">Old: {reasonShift.oldClockOut.toDate ? reasonShift.oldClockOut.toDate().toLocaleTimeString() : '—'}</div>
                ) : null}
              </div>
              <div>
                <div className="text-white/60">Lunch</div>
                <div className="font-medium">
                  {typeof reasonShift?.lunchMin === 'number' ? `${reasonShift.lunchMin} min` : '—'}
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setReasonShift(null)} className="px-4 py-2 rounded bg-slate-700 hover:bg-slate-600">
              Close
            </button>
          </div>
        </div>
      </Modal>
    ) : null}

{/* restore confirm */}
    <Confirm
      open={!!confirmRestore}
      title={`Restore ${confirmRestore?.kind || "item"}`}
      message={`Restore “${confirmRestore?.row?.name || confirmRestore?.row?.roleName || confirmRestore?.row?.storeName || "this item"}”?`}
      onCancel={() => setConfirmRestore(null)}
      onOk={async () => {
        const x = confirmRestore;
        setConfirmRestore(null);
        if (x?.row && x?.kind === 'shift') await restoreShift(x.row);
        else if (x?.row && x?.kind) await restore(x.row, x.kind);
      }}
      okLabel="Restore"
    />
  </div>
);
}